import Loading from "@app/components/Loading";
import { Card } from "@app/shared/components/Card";
import { ColorTag } from "@design-system/DataDisplay/Tag/ColorTag";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { Text } from "@design-system/Typography/Text";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { type AvailableFrameworkCategory } from "@generated/client/graphql";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useMemo, type FC } from "react";
import { Link } from "react-router-dom";
import { useAvailableFrameworkCategories } from "../../data";
import { useAvailableFrameworkCategoriesTranslations } from "./i18n";
import CreateFrameworkProjectModal from "../CreateFrameworkProjectModal";

export const AvailableCategories: FC = () => {
    const { availableFrameworkCategories, isPending } =
        useAvailableFrameworkCategories();
    const { t, keys } = useAvailableFrameworkCategoriesTranslations();
    if (isPending) {
        return <Loading size="large" />;
    }
    return (
        <FlexCol gap="4">
            <Text variant="header3">{t(keys.title)}</Text>
            <Box
                className="grid-cols-[repeat(auto-fit,minmax(272px,_1fr))]"
                display="grid"
                gap="4"
                maxW="full"
            >
                {availableFrameworkCategories?.map((category) => (
                    <CategoryCard
                        key={category.cmsGroupId}
                        category={category}
                    />
                ))}
            </Box>
        </FlexCol>
    );
};

const CategoryCard: FC<{ category: AvailableFrameworkCategory }> = ({
    category: {
        cmsGroupId,
        name,
        description,
        activated,
        imageUrl,
        officialWebsite,
        lastUpdatedProjectId,
    },
}) => {
    const { t, keys } = useAvailableFrameworkCategoriesTranslations();
    const activationStatus = activated ? "active" : "inactive";
    const tagColor = activated ? "#03A365" : "#777777";

    return (
        <Card key={cmsGroupId} h="full" justifyContent="between">
            <FlexCol gap="1">
                <FlexRow
                    as="header"
                    justifyContent="between"
                    alignItems="center"
                >
                    <ColorTag
                        color={tagColor}
                        text={t(keys.card_tag, { context: activationStatus })}
                    />
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={`${name} logo`}
                            className="w-7 h-7 rounded-lg"
                        />
                    ) : (
                        <Box className="w-7 h-7 bg-quarternary" br="full" />
                    )}
                </FlexRow>
                <FlexCol gap="1">
                    <Card.Title>{name}</Card.Title>
                    <Card.Description>{description}</Card.Description>
                </FlexCol>
            </FlexCol>
            <CtaSection
                cmsGroupId={cmsGroupId}
                activated={activated}
                officialWebsite={officialWebsite}
                lastUpdatedProjectId={lastUpdatedProjectId}
            />
        </Card>
    );
};

const CtaSection: FC<{
    cmsGroupId: string;
    activated: boolean;
    officialWebsite: string | null | undefined;
    lastUpdatedProjectId: string | null | undefined;
}> = ({ cmsGroupId, activated, officialWebsite, lastUpdatedProjectId }) => {
    const { t, keys } = useAvailableFrameworkCategoriesTranslations();
    const isBetaTester = useFeatureFlagEnabled("ecovadis2025BetaTesters");
    const isLegacy =
        cmsGroupId === "csrd" || (cmsGroupId === "evs" && !isBetaTester);

    const redirectLink = useMemo(() => {
        if (cmsGroupId === "evs" && !isBetaTester) {
            return `legacy/evs-2023`;
        }

        return `${cmsGroupId}`;
    }, [cmsGroupId, isBetaTester]);

    if (!activated) {
        return (
            <Card.CtaSection as="footer">
                {!!officialWebsite && (
                    <Card.CtaButton variant="text" asChild>
                        <a
                            href={officialWebsite}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:no-underline"
                        >
                            {t(keys.card_cta_learn_more)}
                        </a>
                    </Card.CtaButton>
                )}
                <Card.CtaButton variant="outlined" asChild>
                    <a
                        href="https://share-eu1.hsforms.com/2hA4AUJgJTge3Qn4jSg1tEAfnzjw"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:no-underline"
                    >
                        {t(keys.card_cta_request_access)}
                    </a>
                </Card.CtaButton>
            </Card.CtaSection>
        );
    }
    if (isLegacy) {
        return (
            <Card.CtaSection as="footer">
                {!!officialWebsite && (
                    <Card.CtaButton variant="text" asChild>
                        <a
                            href={officialWebsite}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:no-underline"
                        >
                            {t(keys.card_cta_learn_more)}
                        </a>
                    </Card.CtaButton>
                )}
                <Card.CtaButton asChild>
                    <Link to={redirectLink} className="hover:no-underline">
                        <Button.Icon name="edit" />
                        {t(keys.card_cta_edit_legacy)}
                    </Link>
                </Card.CtaButton>
            </Card.CtaSection>
        );
    }

    return (
        <Card.CtaSection as="footer">
            <Card.CtaButton variant="text" asChild>
                <Link to={redirectLink} className="hover:no-underline">
                    {t(
                        lastUpdatedProjectId
                            ? keys.card_cta_see_projects
                            : keys.card_cta_learn_more,
                    )}
                </Link>
            </Card.CtaButton>
            {lastUpdatedProjectId ? (
                <Card.CtaButton asChild>
                    <Link
                        to={`${cmsGroupId}/${lastUpdatedProjectId}`}
                        className="hover:no-underline"
                    >
                        <Button.Icon name="edit" />
                        {t(keys.card_cta_edit_latest)}
                    </Link>
                </Card.CtaButton>
            ) : (
                <CreateFrameworkProjectModal categoryId={cmsGroupId} />
            )}
        </Card.CtaSection>
    );
};
